import { Button } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { ObservationConsignes } from "../components/observations";

type Observation =
  paths["/api/yards/{yardId}/observations"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: Observation[] };

type DraftState = {
  requiresResponse: boolean;
  responseDraft: string;
  selectedIds: number[];
};

const draftOf = (obs: Observation): DraftState => ({
  requiresResponse: obs.requiresResponse,
  responseDraft: obs.responseDraft ?? "",
  selectedIds: [...obs.selectedDocumentVersionIds],
});

function isDraftDirty(
  draft: DraftState | undefined,
  obs: Observation,
): boolean {
  if (isNil(draft)) return false;
  const saved = draftOf(obs);
  return (
    draft.requiresResponse !== saved.requiresResponse ||
    draft.responseDraft !== saved.responseDraft ||
    draft.selectedIds.length !== saved.selectedIds.length ||
    draft.selectedIds.some((id) => !saved.selectedIds.includes(id))
  );
}

const sectionTitleClass =
  "text-xs font-semibold uppercase tracking-wide text-zinc-500";

/**
 * Screen 023 — "Traiter une observation". For each observation of an exam
 * report, the rang-1 travaux pilot fills the three windows of screen 022:
 * réponse à observation, consignes à l'auteur des documents visés and
 * consignes à un autre prestataire.
 */
export function TraiterObservations() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [drafts, setDrafts] = useState<Record<number, DraftState>>({});
  const [consigneDirty, setConsigneDirty] = useState<Record<number, boolean>>(
    {},
  );
  const [savingId, setSavingId] = useState<number | null>(null);

  // Keeps the other observations' unsaved edits; `resetId` takes the saved
  // values of the observation just saved.
  const fetchObservations = useCallback(
    async (resetId?: number) => {
      if (isNil(yardId)) return;
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/observations",
          { params: { path: { yardId } } },
        );
        if (error || isNil(data)) {
          setState({
            status: "error",
            error: "Erreur lors du chargement des observations",
          });
          return;
        }
        const items = data.data.items;
        setDrafts((prev) =>
          Object.fromEntries(
            items.map((obs) => [
              obs.id,
              obs.id === resetId || isNil(prev[obs.id])
                ? draftOf(obs)
                : prev[obs.id],
            ]),
          ),
        );
        setState({ status: "success", items });
      } catch {
        setState({
          status: "error",
          error: "Erreur lors du chargement des observations",
        });
      }
    },
    [yardId],
  );

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  const items = state.status === "success" ? state.items : [];

  const onConsigneDirtyChange = useCallback(
    (observationId: number, isDirty: boolean) =>
      setConsigneDirty((prev) =>
        prev[observationId] === isDirty
          ? prev
          : { ...prev, [observationId]: isDirty },
      ),
    [],
  );
  useUnsavedChangesGuard(
    items.some((obs) => isDraftDirty(drafts[obs.id], obs)) ||
      Object.values(consigneDirty).some(Boolean),
  );

  const updateDraft = (id: number, patch: Partial<DraftState>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const toggleDocument = (id: number, documentVersionId: number) => {
    const current = drafts[id];
    if (isNil(current)) return;
    updateDraft(id, {
      selectedIds: current.selectedIds.includes(documentVersionId)
        ? current.selectedIds.filter((d) => d !== documentVersionId)
        : [...current.selectedIds, documentVersionId],
    });
  };

  const saveTreatment = async (obs: Observation) => {
    if (isNil(yardId)) return;
    const draft = drafts[obs.id];
    if (isNil(draft)) return;
    setSavingId(obs.id);
    try {
      const { error } = await apiClient.PATCH(
        "/api/yards/{yardId}/observations/{observationId}/treat",
        {
          params: { path: { yardId, observationId: obs.id } },
          body: {
            requiresResponse: draft.requiresResponse,
            responseDraft: draft.responseDraft.trim() || null,
            documentVersionIds: draft.selectedIds,
          },
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de l'enregistrement",
        });
        return;
      }
      toaster.success({
        title: "Observation traitée",
        description: "Le traitement a été enregistré",
      });
      await fetchObservations(obs.id);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement",
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Traiter les observations
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {items.length} observation{items.length > 1 ? "s" : ""}
          </p>
        )}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-none p-4">
          {state.status === "loading" ? (
            <p className="text-sm text-zinc-500">Chargement...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-zinc-500">Aucune observation</p>
          ) : (
            <ul className="space-y-4">
              {items.map((obs) => {
                const draft = drafts[obs.id];
                return (
                  <li
                    key={obs.id}
                    className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {obs.description}
                        </p>
                        <p className="mt-1 text-xs text-zinc-500">
                          Rapport {obs.reportNumero ?? "-"} · Avis{" "}
                          {obs.nAvis ?? "-"}
                          {obs.noticeStatusLabel
                            ? ` · ${obs.noticeStatusLabel}`
                            : ""}
                          {obs.themeName ? ` · ${obs.themeName}` : ""}
                        </p>
                      </div>
                      {obs.treatedAt ? (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                          Traitée
                        </span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                          À traiter
                        </span>
                      )}
                    </div>

                    <section className="mt-4">
                      <h4 className={sectionTitleClass}>
                        Réponse à observation
                      </h4>
                      <div className="mt-2">
                        <label
                          htmlFor={`reponse-${obs.id}`}
                          className="text-xs font-medium text-zinc-500"
                        >
                          Ébauche de réponse
                        </label>
                        <textarea
                          id={`reponse-${obs.id}`}
                          rows={3}
                          value={draft?.responseDraft ?? ""}
                          onChange={(e) =>
                            updateDraft(obs.id, {
                              responseDraft: e.target.value,
                            })
                          }
                          className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                      </div>

                      {obs.concernedDocuments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-zinc-500">
                            Documents concernés par la réponse
                          </p>
                          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                            {obs.concernedDocuments.map((doc) => (
                              <li key={doc.documentVersionId}>
                                <label className="flex items-center gap-1.5 text-xs text-zinc-700 dark:text-zinc-300">
                                  <input
                                    type="checkbox"
                                    checked={
                                      draft?.selectedIds.includes(
                                        doc.documentVersionId,
                                      ) ?? false
                                    }
                                    onChange={() =>
                                      toggleDocument(
                                        obs.id,
                                        doc.documentVersionId,
                                      )
                                    }
                                  />
                                  {doc.label}
                                  {doc.index ? ` (${doc.index})` : ""}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="mt-2 flex items-center justify-between gap-4">
                        <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                          <input
                            type="checkbox"
                            checked={draft?.requiresResponse ?? false}
                            onChange={(e) =>
                              updateDraft(obs.id, {
                                requiresResponse: e.target.checked,
                              })
                            }
                          />
                          Cette observation appelle une réponse
                        </label>
                        <Button
                          type="button"
                          onClick={() => saveTreatment(obs)}
                          disabled={savingId === obs.id}
                        >
                          {savingId === obs.id
                            ? "Enregistrement..."
                            : "Enregistrer le traitement"}
                        </Button>
                      </div>
                    </section>

                    {isNil(yardId) ? null : (
                      <ObservationConsignes
                        yardId={yardId}
                        observationId={obs.id}
                        consignes={obs.consignes}
                        onChanged={() => void fetchObservations()}
                        onDirtyChange={onConsigneDirtyChange}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
